import Image from 'next/image';
import { ReactElement } from 'react';
import { WorkshopEdition } from '../../../types/database';
import Button from '../../Button';
import { ButtonType } from '../../Button/Button.types';
import TitleBarWithComponent from '../../TitleBarWithComponent';

const trainerImageSrc = 'https://images.ctfassets.net/su7lwncim2tu/6pTguQO29Lm2fdWmC2eXDF/163aebdaebcc2d269c8c734307e15947/mateusz-hero.png';

const trainerSectionClassName = `
  relative
  mb-16
  overflow-hidden
  rounded-lg
  px-6
  py-12
  text-white
  [background:radial-gradient(circle_at_78%_10%,rgba(20,165,255,0.22),transparent_34%),linear-gradient(135deg,#00105f_0%,#001773_48%,#0129cf_100%)]

  [@media(min-width:768px)]:p-12
  [@media(min-width:1152px)]:min-h-[390px]
`;

const trainerContentClassName = `
  relative
  z-[3]
  max-w-full

  [@media(min-width:1152px)]:max-w-[650px]
`;

const trainerEyebrowClassName = `
  m-0
  mb-6
  font-heading
  text-[13px]
  leading-none
  tracking-[0.24em]
  text-main-300
  uppercase
`;

const trainerTitleClassName = `
  m-0
  mb-6
  font-heading
  text-[30px]
  font-normal
  leading-[1.12]
  text-white

  [@media(min-width:768px)]:text-[40px]
  [&_strong]:font-normal
  [&_strong]:text-main-300
`;

const trainerDescriptionClassName = `
  m-0
  mb-12
  max-w-full
  text-[17px]
  leading-[1.45]
  text-main-100
`;

const trainerStatsClassName = `
  mb-12
  flex
  flex-wrap
  gap-4
`;

const trainerStatClassName = `
  flex
  min-h-[68px]
  w-full
  flex-[1_1_190px]
  items-center
  gap-2
  rounded-[6px]
  border
  border-[rgba(222,243,255,0.14)]
  bg-white/[0.06]
  p-4

  [@media(min-width:768px)]:w-auto
  [@media(min-width:768px)]:min-w-[190px]
  [@media(min-width:768px)]:flex-[0_1_auto]
`;

const trainerStatIconClassName = `
  flex
  shrink-0
  basis-[34px]
  items-center
  justify-center
  text-main-300

  [&_svg]:h-[30px]
  [&_svg]:w-[30px]
`;

const trainerStatValueClassName = `
  block
  whitespace-nowrap
  font-body
  text-2xl
  font-bold
  leading-none
  text-white

  [@media(min-width:1152px)]:text-[26px]
`;

const trainerStatLabelClassName = `
  mt-[6px]
  block
  text-xs
  leading-[1.2]
  text-main-100
`;

const trainerVisualClassName = `
  pointer-events-none
  absolute
  bottom-0
  right-0
  z-[2]
  hidden
  h-full
  w-[48%]

  [@media(min-width:1152px)]:block
`;

const trainerImageClassName = `
  absolute
  right-0
  bottom-[-40px]
  z-[2]
  h-auto
  max-h-full
  w-[90%]
  object-contain
  object-right-bottom
`;

export interface WorkshopEditionListItem extends WorkshopEdition {
  averageRating?: number | null;
}

export const Wrapper = ({ children }) => (
  <div className="
    flex 
    -mx-4
    flex-col
    mb-12
    mt-12
    flex-wrap

    sm:flex-row
  ">{children}</div>
);

export const WorkshopBox = ({ children }) => (
  <div className="
    grow
    shrink-0
    basis-[50%]
    max-w-[50%]
    mb-8
  ">
    {children}
  </div>
)

interface WorkshopEditionsSectionProps {
  title: ReactElement;
  description: string;
  totalLabel: string;
  editions: WorkshopEditionListItem[];
  averageRatingLabel: string;
  plannedEditionLabel: string;
  openPlaceLabel: string;
  formatEditionDate: (date: string) => string;
}

interface WorkshopAudienceSectionProps {
  title: ReactElement;
  description: string;
  forTitle: string;
  forItems: string[];
  notForTitle: string;
  notForItems: string[];
}

interface WorkshopTrainerSectionProps {
  eyebrow: string;
  title: string;
  name: string;
  description: string;
  experienceValue: string;
  experienceLabel: string;
  teachingSinceValue: string;
  teachingSinceLabel: string;
  rating?: number | null;
  ratingLabel: string;
  actionLabel: string;
}

interface WorkshopEditionCardProps {
  place?: string | null;
  organizer?: string | null;
  date: string;
  dateTime: string;
  rating?: number | null;
  ratingLabel: string;
  plannedEditionLabel: string;
  openPlaceLabel: string;
}

const CalendarIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M2 4.66666H26V26H2V4.66666Z" stroke="currentColor" strokeWidth="3" strokeLinecap="square"/>
    <path d="M26 10H2" stroke="currentColor" strokeWidth="3" strokeLinecap="square"/>
    <path d="M7.33331 4.66667V2" stroke="currentColor" strokeWidth="3" strokeLinecap="square"/>
    <path d="M20.6667 4.66667V2" stroke="currentColor" strokeWidth="3" strokeLinecap="square"/>
    <path d="M18 18H20.6667V20.6667H18V18Z" stroke="currentColor" strokeWidth="3" strokeLinecap="square"/>
  </svg>
);

const StarIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z" />
  </svg>
);

const SchoolIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22 9L12 5L2 9L12 13L22 9V15" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 10.6V16A6 3 0 0 0 18 16V10.6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrainerStat = ({ icon, value, label }: { icon: ReactElement; value: string | ReactElement; label: string }) => (
  <div className={trainerStatClassName}>
    <span className={trainerStatIconClassName}>
      {icon}
    </span>
    <span className="min-w-0">
      <strong className={trainerStatValueClassName}>
        {value}
      </strong>
      <span className={trainerStatLabelClassName}>
        {label}
      </span>
    </span>
  </div>
);

export const WorkshopTrainerSection = ({
  eyebrow,
  title,
  name,
  description,
  experienceValue,
  experienceLabel,
  teachingSinceValue,
  teachingSinceLabel,
  rating,
  ratingLabel,
  actionLabel,
}: WorkshopTrainerSectionProps) => (
  <section className={trainerSectionClassName}>
    <div className={trainerContentClassName}>
      <p className={trainerEyebrowClassName}>
        {eyebrow}
      </p>
      <h2 className={trainerTitleClassName}>
        {title}<br />
        <strong>{name}</strong>
      </h2>
      <p className={trainerDescriptionClassName}>
        {description}
      </p>
      <div className={trainerStatsClassName}>
        <TrainerStat
          icon={<CalendarIcon />}
          value={experienceValue}
          label={experienceLabel}
        />
        <TrainerStat
          icon={<SchoolIcon />}
          value={teachingSinceValue}
          label={teachingSinceLabel}
        />
        <TrainerStat
          icon={<StarIcon />}
          value={<>{rating || '-'} <span className="text-[0.8em]">/ 5</span></>}
          label={ratingLabel}
        />
      </div>
      <Button.L
        href="/about"
        pattern={ButtonType.PRIMARY}
        label={actionLabel}
        className="!inline-flex"
      />
    </div>
    <div className={trainerVisualClassName} aria-hidden="true">
      <Image
        src={trainerImageSrc}
        alt=""
        width={430}
        height={530}
        className={trainerImageClassName}
      />
    </div>
  </section>
);

const EditionsCounter = ({ label, total }: { label: string; total: number }) => (
  <div className="
    flex
    items-start
    gap-5
    text-right
  ">
    <div>
      <span className="
        block
        font-heading
        text-sm
        leading-none
        text-main-300
        uppercase
      ">
        {label}
      </span>
      <strong className="
        block
        mt-2
        font-heading
        text-3xl
        leading-none
        text-main
      ">
        {total}
      </strong>
    </div>
    <span className="
      flex
      h-14
      w-14
      shrink-0
      items-center
      justify-center
      rounded
      bg-main-100
      shadow-[0_10px_24px_rgba(20,165,255,0.22)]
    ">
      <SchoolIcon className="text-main" />
    </span>
  </div>
);

const AudienceColumn = ({ title, items }: { title: string; items: string[] }) => (
  <div className="
    rounded
    border
    border-[#dce5f3]
    bg-white
    p-7
    shadow-[0_8px_22px_rgba(0,23,115,0.08)]
  ">
    <h3 className="
      mb-5
      font-heading
      text-[20px]
      leading-tight
      text-main
    ">
      {title}
    </h3>
    <ul className="
      list-none
      space-y-4
      p-0
    ">
      {items.map(item => (
        <li key={item} className="
          flex
          gap-3
          text-base
          leading-relaxed
          text-main-700
        ">
          <span className="mt-[10px] h-2 w-2 shrink-0 rounded-full bg-main-300" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const WorkshopAudienceSection = ({
  title,
  description,
  forTitle,
  forItems,
  notForTitle,
  notForItems,
}: WorkshopAudienceSectionProps) => (
  <section className="
    mt-20
    mb-20
  ">
    <TitleBarWithComponent title={title} text={description} capitalize={false} />
    <div className="
      grid
      gap-4

      md:grid-cols-2
    ">
      <AudienceColumn title={forTitle} items={forItems} />
      <AudienceColumn title={notForTitle} items={notForItems} />
    </div>
  </section>
);

const WorkshopEditionCard = ({ place, organizer, date, dateTime, rating, ratingLabel, plannedEditionLabel, openPlaceLabel }: WorkshopEditionCardProps) => {
  const editionPlaceText = place || organizer;

  return <li className="
    h-full
    min-h-[168px]
    flex
    flex-col
    rounded
    border
    border-[#dce5f3]
    bg-white
    px-7
    py-6
    shadow-[0_8px_22px_rgba(0,23,115,0.08)]
  ">
    <p className="
      mb-4
      flex
      items-center
      gap-4
      text-sm
      font-medium
      leading-none
      text-main
    ">
      <CalendarIcon className="h-6 w-6 text-main-500" />
      <time dateTime={dateTime}>{date}</time>
    </p>
    <div className="mb-4">
      <h3 className={`
        font-heading
        text-[22px]
        leading-[1.08]
        ${editionPlaceText ? 'text-main' : 'text-main-300'}
      `}>
        {editionPlaceText || openPlaceLabel}
      </h3>
      {place && organizer && <p className="
        mt-2
        text-sm
        font-medium
        leading-tight
        text-main-700
      ">
        {organizer}
      </p>}
    </div>
    {rating > 0 ? <p className="
      mt-auto
      flex
      items-center
      gap-3
      text-sm
      leading-none
      text-main-700
    ">
      <StarIcon className="h-[22px] w-[22px] text-main-500" />
      <strong className="font-body text-base text-main">{rating}</strong>
      <span>{ratingLabel}</span>
    </p> : <p className="
      mt-auto
      text-sm
      font-medium
      leading-none
      text-main-700
    ">
      {plannedEditionLabel}
    </p>}
  </li>
}

export const WorkshopEditionsSection = ({
  title,
  description,
  totalLabel,
  editions,
  averageRatingLabel,
  plannedEditionLabel,
  openPlaceLabel,
  formatEditionDate,
}: WorkshopEditionsSectionProps) => {
  const sortedEditions = [...editions].sort((first, second) => (
    new Date(second.startsAt).getTime() - new Date(first.startsAt).getTime()
  ));

  return (
    <section className="
      mt-20
      mb-20
    ">
      <div className="mb-10">
        <TitleBarWithComponent title={title} text={description} capitalize={false}>
          <EditionsCounter label={totalLabel} total={editions.length} />
        </TitleBarWithComponent>
      </div>
      <ul className="
        grid
        auto-rows-fr
        list-none
        gap-4
        p-0

        md:grid-cols-2
        xl:grid-cols-3
      ">
        {sortedEditions.map(edition => (
          <WorkshopEditionCard
            key={edition.id}
            place={edition.place}
            organizer={edition.organizer}
            date={formatEditionDate(edition.startsAt)}
            dateTime={edition.startsAt}
            rating={edition.averageRating}
            ratingLabel={averageRatingLabel}
            plannedEditionLabel={plannedEditionLabel}
            openPlaceLabel={openPlaceLabel}
          />
        ))}
      </ul>
    </section>
  )
};
